import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import AppStoreBadges from "@/components/AppStoreBadges";
import { ShieldCheck, Wrench, Coins, Heart, Globe2, Users2, Building2, Cpu } from "lucide-react";

function Value({ icon: Icon, title, children }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card hover:border-primary/30 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-display text-4xl text-primary font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function About() {
  return (
    <PublicShell>
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-16" data-testid="about-page">
        {/* Hero */}
        <div className="overline text-secondary">Our story</div>
        <h1 className="mt-2 font-display text-5xl font-light tracking-tight">
          Gadgets are treasures. We treat them that way.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Treasurescare is a UK-based gadget-protection and repair service built by <b className="text-foreground">SRAM &amp; MRAM Limited</b>.
          We help people and businesses keep every phone, laptop, tablet and wearable working — from the moment it's unboxed to the day it's traded in.
        </p>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 py-8 border-y border-border">
          <Stat value="5" label="Tiers" />
          <Stat value="7" label="Repair stages" />
          <Stat value="2" label="Countries" />
          <Stat value="UK" label="Registered" />
        </div>

        {/* Mission */}
        <section className="mt-16">
          <div className="overline text-secondary">Our mission</div>
          <h2 className="mt-2 font-display text-3xl font-light">Repair over replace. Rewards over regret.</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">
            Every year, more than 150 million small electronics are discarded in the UK alone — most of them because a £30 repair felt like too much hassle. We built Treasurescare to change the maths: a single, transparent plan that covers every device you own, ships you a prepaid label, keeps you updated at every stage, and rewards you with gems just for staying protected.
          </p>
          <p className="mt-3 text-foreground/80 leading-relaxed">
            Whether you're a student on our free <b>Finder</b> tier, a family sharing the <b>Household</b> plan, or a business protecting a fleet of laptops, we treat every job the same way: fixed fast, tracked live, returned like new.
          </p>
        </section>

        {/* Values */}
        <section className="mt-16">
          <div className="overline text-secondary">What we stand for</div>
          <h2 className="mt-2 font-display text-3xl font-light">Four principles, no exceptions.</h2>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <Value icon={ShieldCheck} title="Protection people actually use">
              No confusing exclusions. No excess for our top tier. If it's on your account and it stops working, we fix it.
            </Value>
            <Value icon={Wrench} title="Repair-first, always">
              We're workshop operators before we're insurers. Nine out of ten claims come home repaired, not replaced.
            </Value>
            <Value icon={Coins} title="Reward loyalty, not new signups">
              Every action earns gems — signup, renewal, referrals, 5-star reviews. Redeem them in our monthly £100 raffle.
            </Value>
            <Value icon={Heart} title="Radical transparency">
              7-stage repair pipeline visible to you in real time. Verifiable raffle draws. Publicly published policies.
            </Value>
          </div>
        </section>

        {/* Who we serve */}
        <section className="mt-16">
          <div className="overline text-secondary">Who we serve</div>
          <h2 className="mt-2 font-display text-3xl font-light">One plan. Every gadget. Any household or business.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            <Value icon={Users2} title="Families & individuals">
              From a single phone on our free Finder tier to unlimited devices across 6 members on the Household plan.
            </Value>
            <Value icon={Building2} title="Businesses">
              Volume plans for phones, laptops, tablets and audio gear — with admin dashboards, cost centres and CSV import.
            </Value>
            <Value icon={Cpu} title="Retailers & partners">
              Refer & Earn programme with real-time commission tracking, monthly payouts and CSV bulk onboarding.
            </Value>
          </div>
        </section>

        {/* Company */}
        <section className="mt-16 p-8 rounded-2xl border border-border bg-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Globe2 className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <div className="overline text-secondary">Company</div>
              <h3 className="font-display text-2xl mt-1">SRAM &amp; MRAM Limited</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Treasurescare is a service of <b className="text-foreground">SRAM &amp; MRAM Limited</b>, a company registered in England &amp; Wales.
                We operate from London, with a UK repair centre and Malaysia office coming online.
              </p>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5 mt-5 text-sm">
                <div><dt className="inline text-muted-foreground">Company No.</dt> <dd className="inline font-mono">09989681</dd></div>
                <div><dt className="inline text-muted-foreground">VAT No.</dt> <dd className="inline font-mono">GB 466 290 962</dd></div>
                <div className="md:col-span-2"><dt className="inline text-muted-foreground">Registered office:</dt> <dd className="inline">Office Gold Building 7, Floor 5, 566 Chiswick High Road, Chiswick Business Park, London, W4 5YG, United Kingdom</dd></div>
                <div className="md:col-span-2"><dt className="inline text-muted-foreground">Contact:</dt> <dd className="inline"><a href="mailto:support@treasurescare.com" className="text-primary hover:underline">support@treasurescare.com</a></dd></div>
              </dl>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 text-center py-12 border-t border-border">
          <h2 className="font-display text-3xl font-light">Ready to treasure your gadgets?</h2>
          <p className="mt-3 text-muted-foreground">Start free with the Finder plan — no card required.</p>
          <div className="flex justify-center gap-3 mt-6">
            <Link to="/signup">
              <Button size="lg" data-testid="about-cta-signup">Create free account</Button>
            </Link>
            <Link to="/#plans">
              <Button size="lg" variant="outline" data-testid="about-cta-plans">Compare plans</Button>
            </Link>
          </div>
          <div className="mt-8 flex justify-center">
            <AppStoreBadges />
          </div>
          <div className="mt-6 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">Privacy policy</Link> · <Link to="/terms" className="hover:text-foreground">Terms &amp; conditions</Link>
          </div>
        </section>
      </article>
    </PublicShell>
  );
}
